import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { getElapsedHoursInCurrentMonth } from '../../utils/timeUtils';

function FleetMonthlyAvailabilityChart({
  outageDays,
  productionByMonth,
  year,
  nominalCapacityMW = null,
  currentDate = null,
}) {
  // Build monthly aggregates from daily outage series
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const agg = months.map(m => ({ month: m, days: 0, capSum: 0, availSum: 0 }));
  let cutoffMonth = 12;
  if (currentDate) {
    const d = new Date(currentDate);
    if (Number.isFinite(d.getTime()) && d.getFullYear() === Number(year)) {
      cutoffMonth = d.getMonth() + 1; // 1..12
    }
  }
  (outageDays || []).forEach(row => {
    if (!row || typeof row.period !== 'string') return;
    if (!row.period.startsWith(String(year))) return;
    const m = Number(row.period.slice(5, 7));
    if (!Number.isFinite(m) || m < 1 || m > 12) return;
    if (m > cutoffMonth) return; // trim months beyond selected date
    const cap = Number(row.capacity) || 0;
    const out = Number(row.outage) || 0;
    agg[m - 1].capSum += cap;
    agg[m - 1].availSum += Math.max(0, cap - out);
    agg[m - 1].days += 1;
  });

  const productionMap = Object.fromEntries(
    (productionByMonth || []).map(d => [
      Number(d.month),
      Number(d.production) || 0,
    ]),
  );

  const full = agg.map((a, idx) => {
    const m = idx + 1;
    const days = a.days || 0;
    const avgCap = days > 0 ? a.capSum / days : 0; // MW
    const avgAvail = days > 0 ? a.availSum / days : 0; // MW
    const avgOut = Math.max(0, avgCap - avgAvail);
    const totalHours = new Date(year, m, 0).getDate() * 24;

    // For current month, use elapsed hours for more accurate load calculations
    const now = moment();
    const isCurrentMonth = now.year() === year && now.month() + 1 === m;
    const effectiveHours = isCurrentMonth
      ? getElapsedHoursInCurrentMonth(year, m)
      : totalHours;

    const prodMWh = productionMap[m] || 0;
    const denomCap =
      typeof nominalCapacityMW === 'number' && nominalCapacityMW > 0
        ? nominalCapacityMW
        : avgCap;
    const loadPct =
      denomCap > 0 && effectiveHours > 0
        ? Math.round((prodMWh / (denomCap * effectiveHours)) * 100)
        : null;
    const adjustedLoadPct =
      avgAvail > 0 && effectiveHours > 0
        ? Math.round((prodMWh / (avgAvail * effectiveHours)) * 1000) / 10
        : null; // one decimal place

    return {
      month: m,
      avgCapacityMW: avgCap,
      avgAvailableMW: avgAvail,
      avgOutageMW: avgOut,
      loadPct,
      adjustedLoadPct,
      isCurrentMonth: isCurrentMonth,
    };
  });
  const data =
    cutoffMonth < 12 ? full.filter(d => d.month <= cutoffMonth) : full;

  const tickMonth = v =>
    new Date(0, v - 1).toLocaleString('default', { month: 'short' });
  const fmtGW = v => ((Number(v) || 0) / 1000).toFixed(1);

  const formatTooltipValue = (value, name) => {
    if (name === 'Load' || name === 'Adjusted Load') return [`${value}%`, name];
    const gw = ((Number(value) || 0) / 1000).toFixed(2);
    return [`${gw} GW`, name];
  };
  const formatTooltipLabel = v => {
    const d = new Date(0, v - 1);
    return `${d.toLocaleString('default', { month: 'long' })} ${year}`;
  };

  return (
    <div className="FleetMonthlyAvailabilityChart">
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="availFillFleet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#26a65b" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#26a65b" stopOpacity={0.2} />
              </linearGradient>
              <linearGradient id="outFillFleet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f39c12" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#f39c12" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={tickMonth} />
            <YAxis
              yAxisId="mw"
              tickFormatter={fmtGW}
              label={{ value: 'GW', angle: -90, position: 'insideLeft' }}
            />
            <YAxis
              yAxisId="pct"
              orientation="right"
              domain={[0, 100]}
              tickFormatter={v => `${v}`}
              label={{ value: 'Load (%)', angle: -90, position: 'insideRight' }}
            />
            <Tooltip
              formatter={formatTooltipValue}
              labelFormatter={formatTooltipLabel}
            />
            <Legend />
            <Area
              yAxisId="mw"
              type="monotone"
              dataKey="avgOutageMW"
              name="Outage"
              stackId="stack"
              stroke="#f39c12"
              fillOpacity={1}
              fill="url(#outFillFleet)"
            />
            <Area
              yAxisId="mw"
              type="monotone"
              dataKey="avgAvailableMW"
              name="Available"
              stackId="stack"
              stroke="#26a65b"
              fillOpacity={1}
              fill="url(#availFillFleet)"
            />
            <Line
              yAxisId="pct"
              type="monotone"
              dataKey="loadPct"
              name="Load"
              stroke="#3498db"
              dot={false}
            />
            <Line
              yAxisId="pct"
              type="monotone"
              dataKey="adjustedLoadPct"
              name="Adjusted Load"
              stroke="#9b59b6"
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="FleetMonthlyAvailabilityChart__legend">
        <div>
          <strong>Load:</strong> power divided by the capacity (before any
          unavailabilities)
        </div>
        <div>
          <strong>Adjusted Load:</strong> power produced divided by the
          available power
        </div>
      </div>
    </div>
  );
}

FleetMonthlyAvailabilityChart.propTypes = {
  outageDays: PropTypes.array, // daily aggregated (us-nuclear-outages) or generator sum
  productionByMonth: PropTypes.array, // [{ month, production }]
  year: PropTypes.number.isRequired,
  nominalCapacityMW: PropTypes.number,
  currentDate: PropTypes.string,
};

export default FleetMonthlyAvailabilityChart;
